import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { interviewsAPI } from '../lib/api';
import { usePlan } from '../hooks/usePlan';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const TEMPLATE_ICONS = {
  technical: { color: '#0076A3', bg: 'rgba(30,104,255,0.12)', icon: '</>' },
  behavioral: { color: '#10B981', bg: 'rgba(16,185,129,0.12)', icon: '\u{1F465}' },
  situational: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: '\u{1F4A1}' },
};

const STATUS_STYLES = {
  active: { label: 'active', background: 'rgba(16,185,129,0.15)', color: '#059669', border: '1px solid rgba(16,185,129,0.3)' },
  generating: { label: 'generating…', background: 'rgba(245,158,11,0.15)', color: '#B45309', border: '1px solid rgba(245,158,11,0.35)' },
  failed: { label: 'failed', background: 'rgba(239,68,68,0.15)', color: '#DC2626', border: '1px solid rgba(239,68,68,0.35)' },
};

export default function InterviewListPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { isStarter } = usePlan();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('technical');
  const [allowMultiple, setAllowMultiple] = useState(false);

  const pollRef = useRef(null);
  const prevStatusRef = useRef({});
  const clearPoll = () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };

  const loadInterviews = useCallback(async () => {
    try {
      const res = await interviewsAPI.listForJob(jobId);
      const data = res.data || [];
      // Toast when an interview transitions from generating -> ready/failed.
      data.forEach((iv) => {
        const prev = prevStatusRef.current[iv._id];
        if (prev === 'generating' && iv.status === 'active') toast.success('Interview questions ready!');
        else if (prev === 'generating' && iv.status === 'failed') toast.error('Interview generation failed. Please try again.');
      });
      prevStatusRef.current = Object.fromEntries(data.map((iv) => [iv._id, iv.status]));
      setInterviews(data);
      clearPoll();
      if (data.some((iv) => iv.status === 'generating')) {
        pollRef.current = setTimeout(() => { loadInterviews(); }, 3000);
      }
    } catch (err) { toast.error('Failed to load interviews'); }
    finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { loadInterviews(); return () => clearPoll(); }, [loadInterviews]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await interviewsAPI.create(jobId, { template: selectedTemplate, num_questions: 5, allow_multiple_sessions: allowMultiple });
      toast.success('Interview is being generated — questions will appear in a few seconds.');
      setShowCreate(false);
      loadInterviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create interview');
    } finally { setCreating(false); }
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/join-interview/${code}`);
    toast.success('Interview link copied!');
  };

  if (loading) return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',color:'hsl(var(--muted-foreground))' }}>Loading...</div>;

  return (
    <div data-testid="interview-list-page">
      <div className="page-header">
        <div style={{ display:'flex',alignItems:'center',gap:'1rem' }}>
          <button onClick={() => navigate(`/jobs/${jobId}`)} style={{ background:'none',border:'none',color:'hsl(var(--muted-foreground))',cursor:'pointer',padding:'0.25rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div>
            <h1 className="page-title">AI Interviews</h1>
            <p className="page-subtitle">{interviews.length} interview{interviews.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        {isStarter ? (
          <Button data-testid="interview-upgrade-btn" onClick={() => navigate('/payment?plan=professional_monthly')} style={{ background:'#F2600C',color:'#fff',borderRadius:10,fontWeight:600 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight:6 }}><path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7.4L12 17l-6.3 4.4L8 14 2 9.4h7.6z"/></svg>
            Upgrade to Growth
          </Button>
        ) : (
          <Button onClick={() => setShowCreate(true)} style={{ background:'#0076A3',color:'#fff',borderRadius:10,fontWeight:600 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight:6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Create Interview
          </Button>
        )}
      </div>

      {isStarter && (
        <div data-testid="interview-upgrade-banner" style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'1rem',flexWrap:'wrap',background:'linear-gradient(90deg,#F5F3FF,#EEF2FF)',border:'1px solid #DDD6FE',borderRadius:14,padding:'1rem 1.25rem',marginBottom:'1.5rem' }}>
          <div>
            <div style={{ fontWeight:700,color:'#5B21B6',fontSize:'0.9375rem' }}>AI Video Interviews are a Growth feature</div>
            <div style={{ fontSize:'0.8125rem',color:'#F05000',marginTop:'0.25rem' }}>Your Starter plan doesn’t include AI Video Interviews. Upgrade to Growth to create and send interviews.</div>
          </div>
          <Button data-testid="interview-upgrade-banner-btn" onClick={() => navigate('/payment?plan=professional_monthly')} style={{ background:'#F2600C',color:'#fff',borderRadius:999,fontWeight:600,whiteSpace:'nowrap' }}>Upgrade to Growth</Button>
        </div>
      )}

      {interviews.length === 0 ? (
        <div className="empty-state" style={{ paddingTop:'4rem' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width:56,height:56,marginBottom:'1.5rem' }}>
            <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
          </svg>
          <h3 style={{ fontSize:'1.125rem',fontWeight:600,color:'#FAFAFA',marginBottom:'0.5rem' }}>No interviews yet</h3>
          <p style={{ marginBottom:'1.5rem' }}>{isStarter ? 'AI Video Interviews are available on the Growth plan.' : 'Create an AI-powered interview with generated questions.'}</p>
          {isStarter ? (
            <Button data-testid="interview-empty-upgrade-btn" onClick={() => navigate('/payment?plan=professional_monthly')} style={{ background:'#F2600C',color:'#fff',borderRadius:10,fontWeight:600 }}>Upgrade to Growth</Button>
          ) : (
            <Button onClick={() => setShowCreate(true)} style={{ background:'#0076A3',color:'#fff',borderRadius:10,fontWeight:600 }}>Create First Interview</Button>
          )}
        </div>
      ) : (
        <div style={{ display:'flex',flexDirection:'column',gap:'1rem' }}>
          {interviews.map(iv => {
            const tmpl = TEMPLATE_ICONS[iv.template] || TEMPLATE_ICONS.technical;
            const st = STATUS_STYLES[iv.status] || STATUS_STYLES.active;
            const isGenerating = iv.status === 'generating';
            const isFailed = iv.status === 'failed';
            return (
              <Card key={iv._id} data-testid={`interview-card-${iv._id}`} style={{ background:'hsl(var(--card))',border:'1px solid hsl(var(--border))',borderRadius:14,padding:'1.5rem' }}>
                <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'0.75rem' }}>
                  <div style={{ display:'flex',alignItems:'center',gap:'0.75rem' }}>
                    <div style={{ width:40,height:40,borderRadius:10,background:tmpl.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.125rem' }}>{tmpl.icon}</div>
                    <div>
                      <h3 style={{ fontFamily:'Cabinet Grotesk',fontSize:'1.0625rem',fontWeight:600 }}>{iv.template_name}</h3>
                      <p style={{ fontSize:'0.75rem',color:'hsl(var(--muted-foreground))' }}>{iv.job_title}</p>
                    </div>
                  </div>
                  <Badge data-testid={`interview-status-${iv._id}`} style={{ background:st.background,color:st.color,border:st.border,borderRadius:999 }}>{st.label}</Badge>
                </div>
                <div style={{ display:'flex',gap:'1.5rem',marginBottom:'1rem',fontSize:'0.8125rem',color:'hsl(var(--muted-foreground))' }}>
                  {isGenerating ? (
                    <span style={{ display:'inline-flex',alignItems:'center',gap:6,color:'#B45309' }} data-testid={`interview-generating-${iv._id}`}>
                      <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                      Generating questions…
                    </span>
                  ) : isFailed ? (
                    <span style={{ color:'#DC2626' }}>Generation failed</span>
                  ) : (
                    <span>{(iv.questions||[]).length} questions</span>
                  )}
                  <span>{iv.session_count||0} sessions</span>
                </div>
                <div style={{ display:'flex',gap:'0.5rem' }}>
                  <Button variant="outline" onClick={() => navigate(`/interviews/${iv._id}/results`)} style={{ borderRadius:10,fontSize:'0.8125rem' }}>View Results ({iv.session_count||0})</Button>
                  <Button data-testid={`copy-link-${iv._id}`} onClick={() => copyLink(iv.access_code)} disabled={iv.status !== 'active'} style={{ background:iv.status !== 'active' ? 'hsl(var(--muted))' : '#0076A3',color:'#fff',borderRadius:10,fontSize:'0.8125rem',fontWeight:600,opacity:iv.status !== 'active' ? 0.55 : 1,cursor:iv.status !== 'active' ? 'not-allowed' : 'pointer' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight:6 }}><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                    Copy Candidate Link
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent style={{ background:'hsl(240 6% 10%)',border:'1px solid hsl(240 5% 18%)',borderRadius:14,maxWidth:480 }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:'Cabinet Grotesk' }}>Create AI Interview</DialogTitle>
            <DialogDescription style={{ fontSize:'0.875rem',color:'hsl(var(--muted-foreground))' }}>Choose an interview type. AI will generate tailored questions.</DialogDescription>
          </DialogHeader>
          <div style={{ marginBottom:'0.5rem' }} />
          <div style={{ display:'flex',flexDirection:'column',gap:'0.625rem',marginBottom:'1.5rem' }}>
            {Object.entries(TEMPLATE_ICONS).map(([key, val]) => (
              <button key={key} onClick={() => setSelectedTemplate(key)} style={{
                display:'flex',alignItems:'center',gap:'0.875rem',padding:'1rem',borderRadius:12,cursor:'pointer',textAlign:'left',width:'100%',
                border:`1px solid ${selectedTemplate===key ? val.color : 'hsl(240 5% 18%)'}`,
                background:selectedTemplate===key ? val.bg : 'hsl(240 6% 7%)',color:'#FAFAFA',transition:'border-color 0.15s'
              }}>
                <span style={{ fontSize:'1.25rem' }}>{val.icon}</span>
                <div>
                  <div style={{ fontWeight:600,fontSize:'0.9375rem',textTransform:'capitalize' }}>{key}</div>
                  <div style={{ fontSize:'0.75rem',color:'hsl(var(--muted-foreground))' }}>
                    {key==='technical'?'Technical skills, system design, coding':
                     key==='behavioral'?'Soft skills, teamwork, leadership':
                     'Decision-making, real-world scenarios'}
                  </div>
                </div>
                {selectedTemplate===key && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={val.color} strokeWidth="2" style={{ marginLeft:'auto' }}><polyline points="20 6 9 17 4 12"/></svg>}
              </button>
            ))}
          </div>
          <label data-testid="allow-multiple-sessions-toggle" style={{ display:'flex',alignItems:'flex-start',gap:'0.625rem',padding:'0.875rem',borderRadius:12,border:'1px solid hsl(240 5% 18%)',background:'hsl(240 6% 7%)',cursor:'pointer',marginBottom:'1.25rem' }}>
            <input type="checkbox" checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)} style={{ marginTop:'0.15rem',accentColor:'#0076A3',width:16,height:16 }} />
            <div>
              <div style={{ fontWeight:600,fontSize:'0.875rem',color:'#FAFAFA' }}>Allow retakes</div>
              <div style={{ fontSize:'0.75rem',color:'hsl(var(--muted-foreground))' }}>Let a candidate start this interview more than once. Off by default — each candidate gets a single attempt.</div>
            </div>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} style={{ borderRadius:10 }}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} style={{ background:'#0076A3',color:'#fff',borderRadius:10,fontWeight:600 }}>
              {creating ? 'Generating...' : 'Generate Interview'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
